import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box } from '@mui/material';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Favorite from '@mui/icons-material/Favorite';

import { MentionText } from '../MentionText';
import { RankBadge } from '../RankBadge';
import { getFormatViews, getTimeAgo, getAvatarUrl } from '../../helpers';
import { Comment } from '../../types';
import styles from './style';

interface CommentRowProps {
  comment: Comment;
  // The parent comment of a reply: the reply is indented.
  parent?: Comment;
  // Opened from a notification: the row is highlighted for a moment.
  highlighted?: boolean;
  onLike: (comment: Comment, parent?: Comment) => void;
  onReply: (comment: Comment, parent?: Comment) => void;
  onOpenUser: (userId: string) => void;
}

const CommentRow: React.FC<CommentRowProps> = React.memo((props) => {
  const {
    comment,
    parent,
    highlighted,
    onLike,
    onReply,
    onOpenUser,
  } = props;
  const { t } = useTranslation();

  const avatarUrl = useMemo(() => getAvatarUrl(comment.user ? comment.user.avatar : ''), [comment.user]);

  const onClickLike = useCallback(() => {
    onLike(comment, parent);
  }, [comment, onLike, parent]);

  const onClickReply = useCallback(() => {
    onReply(comment, parent);
  }, [comment, onReply, parent]);

  const onClickUser = useCallback(() => {
    if (comment.user) {
      onOpenUser(comment.user.id);
    }
  }, [comment.user, onOpenUser]);

  const onOpenReceiver = useCallback(() => {
    if (comment.receiver) {
      onOpenUser(comment.receiver.id);
    }
  }, [comment.receiver, onOpenUser]);

  const rankUser = useMemo(() => (comment.user ? {
    id: comment.user.id,
    username: comment.user.username,
    rank: comment.user.rank,
  } : undefined), [comment.user]);

  const rowSx = useMemo(() => {
    const base = parent ? styles.rowReply : styles.row;
    return highlighted ? { ...base, ...styles.rowHighlighted } : base;
  }, [highlighted, parent]);

  return (
    <Box data-comment-id={comment.id} sx={rowSx}>
      <Avatar alt="" src={avatarUrl} onClick={onClickUser} sx={parent ? styles.avatarReply : styles.avatar} />
      <Box sx={styles.body}>
        {/* No badge for a recruit in the comments, like the app. */}
        <Box sx={styles.userLine}>
          <Box component="button" type="button" onClick={onClickUser} sx={styles.username}>
            {comment.user ? comment.user.username : t('admin.notifs.deletedUser')}
          </Box>
          {rankUser && <RankBadge rank={rankUser.rank} user={rankUser} hideRecruit />}
        </Box>
        <Box component="span" sx={styles.message}>
          {parent && comment.receiver && comment.receiver.id !== parent.userId && (
            <Box component="button" type="button" onClick={onOpenReceiver} sx={styles.mention}>{`@${comment.receiver.username} `}</Box>
          )}
          <MentionText text={comment.message} mentions={comment.mentionedUsers} onOpenUser={onOpenUser} />
        </Box>
        <Box sx={styles.meta}>
          {getTimeAgo(comment.createdAt, t)}
          <Box component="button" type="button" onClick={onClickReply} sx={styles.reply}>{t('admin.feed.reply')}</Box>
        </Box>
      </Box>
      <Box component="button" type="button" onClick={onClickLike} aria-label={t('admin.feed.like')} sx={comment.isLiked ? styles.likeActive : styles.like}>
        {comment.isLiked ? <Favorite /> : <FavoriteBorder />}
        {comment.likes > 0 && getFormatViews(comment.likes)}
      </Box>
    </Box>
  );
});

export default CommentRow;
